import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MAX_RECEIPTS_PER_PACKAGE, checkReceiptFile } from '@mypoz/shared';

import { UploadImageError, uploadImage } from '@/lib/upload-image.js';

/** One receipt picked for one package (MYP-96). */
export interface ReceiptSlot {
  readonly id: string;
  readonly name: string;
  readonly status: 'uploading' | 'uploaded' | 'failed';
  /** The vendor's stored path, once uploaded. */
  readonly path?: string;
  /**
   * Why it did not upload: refused before sending (`type`, `size`), or by the vendor / the network
   * (`format`, `failed`). Only `failed` can be retried as it is.
   */
  readonly problem?: 'type' | 'size' | 'format' | 'failed';
}

/**
 * The purchase receipts picked on Step 1, uploaded as soon as they are picked (MYP-96).
 *
 * Each file is checked first (JPG/PNG, up to 20 MB) and never encoded if it fails. Then it is
 * shrunk and sent to `uploadImg`, and the row shows it uploading, uploaded, or failed with a retry. Only the stored
 * paths go with the order (`paths`), so nothing here lives only in the browser any more, and there
 * are no object URLs to leak. Up to three per package.
 */
export function useReceiptUploads() {
  const [slots, setSlots] = useState<Readonly<Record<string, readonly ReceiptSlot[]>>>({});
  /** The files behind failed uploads, kept only so Retry can send the same file again. */
  const files = useRef(new Map<string, File>());
  const nextId = useRef(0);
  /** The latest slots, for decisions that cannot wait for a render. */
  const latest = useRef(slots);
  useEffect(() => {
    latest.current = slots;
  }, [slots]);

  const update = useCallback((packageId: string, slotId: string, patch: Partial<ReceiptSlot>) => {
    setSlots((current) => ({
      ...current,
      [packageId]: (current[packageId] ?? []).map((slot) =>
        slot.id === slotId ? { ...slot, ...patch } : slot,
      ),
    }));
  }, []);

  const send = useCallback(
    (packageId: string, slotId: string, file: File) => {
      update(packageId, slotId, { status: 'uploading', problem: undefined });
      uploadImage(file).then(
        (path) => {
          files.current.delete(slotId);
          update(packageId, slotId, { status: 'uploaded', path });
        },
        (cause: unknown) => {
          console.error('[orders] receipt upload failed', cause);
          const problem = cause instanceof UploadImageError ? cause.failure : 'failed';
          update(packageId, slotId, { status: 'failed', problem });
        },
      );
    },
    [update],
  );

  const attach = useCallback(
    (packageId: string, file: File) => {
      // Read from the ref, not inside a state updater: whether to upload must be decided now.
      const existing = latest.current[packageId] ?? [];
      // A refused pick is replaced by the next one rather than piling up.
      const kept = existing.filter((slot) => slot.status !== 'failed' || slot.problem === 'failed');
      if (kept.length >= MAX_RECEIPTS_PER_PACKAGE) return;

      const slotId = `r${nextId.current++}`;
      const refused = checkReceiptFile(file);
      const slot: ReceiptSlot = refused
        ? { id: slotId, name: file.name, status: 'failed', problem: refused }
        : { id: slotId, name: file.name, status: 'uploading' };
      const next = { ...latest.current, [packageId]: [...kept, slot] };
      latest.current = next;
      setSlots(next);

      if (!refused) {
        files.current.set(slotId, file);
        send(packageId, slotId, file);
      }
    },
    [send],
  );

  const retry = useCallback(
    (packageId: string, slotId: string) => {
      const file = files.current.get(slotId);
      if (file) send(packageId, slotId, file);
    },
    [send],
  );

  const remove = useCallback((packageId: string, slotId: string) => {
    files.current.delete(slotId);
    setSlots((current) => ({
      ...current,
      [packageId]: (current[packageId] ?? []).filter((slot) => slot.id !== slotId),
    }));
  }, []);

  /** Stored paths per package, for `packageinfo`. */
  const paths = useMemo(() => {
    const out: Record<string, readonly string[]> = {};
    for (const [packageId, list] of Object.entries(slots)) {
      out[packageId] = list.flatMap((slot) =>
        slot.status === 'uploaded' && slot.path ? [slot.path] : [],
      );
    }
    return out;
  }, [slots]);

  /**
   * The packages with an upload still in flight. Per package, so the caller can count only the
   * ticked ones: a package unticked mid-upload hides its row, and an upload nobody can see must
   * not hold Step 1 (its slots are kept, so ticking it again brings them back).
   */
  const uploadingIds = useMemo(() => {
    const ids = new Set<string>();
    for (const [packageId, list] of Object.entries(slots)) {
      if (list.some((slot) => slot.status === 'uploading')) ids.add(packageId);
    }
    return ids as ReadonlySet<string>;
  }, [slots]);

  return { slots, paths, uploadingIds, attach, retry, remove };
}
